import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OPTICAL_PIPELINE_STAGES,
  type OpticalReport,
  type OpticalReportQuery,
} from '@optiflow/types';
import { Card } from '@/components/ui/Card';
import { OpticalRecords, type OpticalRecordsRequest } from './OpticalRecords';
import { Section, SimpleTable, Stat, CountText, DaysText, ViewRecords } from './ReportBlocks';

const InventoryCountChart = lazy(() => import('./InventoryCountChart'));

/**
 * The Optical tab.
 *
 * What is open now — the same figures as the Overview — apart from what happened in the period, which
 * is read from each job's lifecycle history. A job is timed when its ORDERED and DELIVERED rows are both
 * reliable, gaps between them or not; the rest are counted and named, never estimated. No prescription or other clinical value appears here.
 */
export function OpticalContent({
  report,
  query,
}: {
  report: OpticalReport;
  query: OpticalReportQuery;
}) {
  const { t } = useTranslation();
  const count = (value: number) => <CountText value={value} />;
  const days = (seconds: number | null) => <DaysText seconds={seconds} />;
  const [open, setOpen] = useState<OpticalRecordsRequest | null>(null);
  const view = (request: OpticalRecordsRequest) => (
    <ViewRecords title={request.title} onOpen={() => setOpen(request)} />
  );
  const stage = (status: string) => t(`sales.opticalJobs.status.${status}`);
  const col = (key: string) => t(`reports.optical.columns.${key}`);
  const { pipeline, turnaround, supply, readyMessages } = report;

  const records = open ? (
    <OpticalRecords
      key={`${open.kind}-${open.status ?? ''}-${open.attention ?? ''}-${open.lifecycle ?? ''}-${open.messageState ?? ''}`}
      query={query}
      request={open}
      customerIdentity={report.customerIdentity}
      messageState={readyMessages !== undefined}
      onClose={() => setOpen(null)}
    />
  ) : null;
  const periodRecords = open?.kind === 'DELIVERED_JOBS';

  return (
    <div className="space-y-6">
      <Section
        id="optical-now"
        title={t('reports.optical.nowTitle')}
        note={t('reports.optical.snapshotNote')}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {OPTICAL_PIPELINE_STAGES.map((status) => (
            <Stat
              key={status}
              label={stage(status)}
              action={view({ kind: 'ACTIVE_JOBS', status, title: stage(status) })}
            >
              {count(pipeline.pipeline[status])}
            </Stat>
          ))}
          <Stat label={t('reports.optical.kpis.active')}>{count(report.activeJobs)}</Stat>
        </div>
        <Card className="min-w-0 p-4">
          <h3 className="text-ink font-semibold">{t('reports.optical.jobsByStage')}</h3>
          <Suspense fallback={null}>
            <InventoryCountChart
              label={t('reports.optical.jobsByStage')}
              data={OPTICAL_PIPELINE_STAGES.map((status) => ({
                label: stage(status),
                value: pipeline.pipeline[status],
              }))}
            />
          </Suspense>
        </Card>
        <h3 className="text-ink font-semibold">{t('reports.optical.attentionTitle')}</h3>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Stat
            label={t('reports.optical.kpis.readyPast')}
            hint={t('reports.optical.hints.moreThan', {
              count: pipeline.readyForCollectionAttentionDays,
            })}
            action={view({
              kind: 'ATTENTION_JOBS',
              attention: 'READY_PAST',
              title: t('reports.optical.kpis.readyPast'),
            })}
          >
            {count(pipeline.readyPastThreshold)}
          </Stat>
          <Stat
            label={t('reports.optical.kpis.labPast')}
            hint={t('reports.optical.hints.moreThan', { count: pipeline.labStageAttentionDays })}
            action={view({
              kind: 'ATTENTION_JOBS',
              attention: 'LAB_PAST',
              title: t('reports.optical.kpis.labPast'),
            })}
          >
            {count(pipeline.labPastThreshold)}
          </Stat>
          <Stat
            label={t('reports.optical.kpis.orderedPast')}
            hint={t('reports.optical.hints.moreThan', {
              count: pipeline.orderedNotSentAttentionDays,
            })}
            action={view({
              kind: 'ATTENTION_JOBS',
              attention: 'ORDERED_PAST',
              title: t('reports.optical.kpis.orderedPast'),
            })}
          >
            {count(pipeline.orderedNotSentPastThreshold)}
          </Stat>
        </div>
      </Section>

      {records && !periodRecords ? records : null}

      <Card className="min-w-0 p-4">
        <h3 className="text-ink font-semibold">{t('reports.optical.byBranch')}</h3>
        <SimpleTable
          headers={[
            col('branch'),
            ...OPTICAL_PIPELINE_STAGES.map(stage),
            t('reports.optical.kpis.readyPast'),
            t('reports.optical.kpis.labPast'),
            t('reports.optical.kpis.orderedPast'),
          ]}
          ends={[1, 2, 3, 4, 5, 6, 7, 8]}
          rows={report.byBranch.map((row) => ({
            key: row.branchId,
            cells: [
              row.branchName,
              ...OPTICAL_PIPELINE_STAGES.map((status) => count(row.pipeline[status])),
              count(row.readyPastThreshold),
              count(row.labPastThreshold),
              count(row.orderedNotSentPastThreshold),
            ],
          }))}
        />
      </Card>

      <Card className="min-w-0 p-4">
        <h3 className="text-ink font-semibold">{t('reports.optical.byLab')}</h3>
        <p className="text-muted text-xs">{t('reports.optical.labNote')}</p>
        {report.byLab.length === 0 ? (
          <p className="text-muted mt-3 text-sm">{t('reports.optical.empty')}</p>
        ) : (
          <SimpleTable
            headers={[
              col('lab'),
              col('atLab'),
              col('back'),
              col('labPast'),
              col('timed'),
              col('average'),
            ]}
            ends={[1, 2, 3, 4, 5]}
            rows={report.byLab.map((row) => ({
              key: row.labId ?? 'none',
              cells: [
                row.labName ?? t('reports.optical.noLab'),
                count(row.atLab),
                count(row.back),
                count(row.labPastThreshold),
                count(row.turnaround.jobs),
                days(row.turnaround.averageSeconds),
              ],
            }))}
          />
        )}
      </Card>

      {readyMessages ? (
        <Card className="min-w-0 p-4">
          <h3 className="text-ink font-semibold">{t('reports.optical.messagesTitle')}</h3>
          <p className="text-muted text-xs">{t('reports.optical.messagesNote')}</p>
          <SimpleTable
            headers={[col('message'), col('jobs'), '']}
            ends={[1]}
            rows={readyMessages.map((row) => {
              const label = t(`reports.optical.messageState.${row.state}`);
              return {
                key: row.state,
                cells: [
                  label,
                  count(row.jobs),
                  row.jobs > 0
                    ? view({
                        kind: 'ACTIVE_JOBS',
                        status: 'READY_FOR_COLLECTION',
                        messageState: row.state,
                        title: `${stage('READY_FOR_COLLECTION')} — ${label}`,
                      })
                    : null,
                ],
              };
            })}
          />
        </Card>
      ) : null}

      <Section
        id="optical-period"
        title={t('reports.optical.periodTitle')}
        note={t('reports.optical.periodNote')}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Stat label={t('reports.optical.kpis.created')}>{count(report.flows.created)}</Stat>
          <Stat label={t('reports.optical.kpis.delivered')}>{count(report.flows.delivered)}</Stat>
          <Stat label={t('reports.optical.kpis.cancelled')}>{count(report.flows.cancelled)}</Stat>
        </div>
        <h3 className="text-ink font-semibold">{t('reports.optical.turnaroundTitle')}</h3>
        <p className="text-muted text-xs">{t('reports.optical.turnaroundNote')}</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <Stat
            label={t('reports.optical.kpis.averageTurnaround')}
            hint={t('reports.optical.hints.timed', { count: turnaround.jobs })}
            action={view({
              kind: 'DELIVERED_JOBS',
              lifecycle: 'TIMED',
              title: t('reports.optical.kpis.averageTurnaround'),
            })}
          >
            {days(turnaround.averageSeconds)}
          </Stat>
          <Stat label={t('reports.optical.kpis.medianTurnaround')}>
            {days(turnaround.medianSeconds)}
          </Stat>
          <Stat
            label={t('reports.optical.lifecycle.START_NOT_RECORDED')}
            hint={t('reports.optical.hints.startNotRecorded')}
            action={view({
              kind: 'DELIVERED_JOBS',
              lifecycle: 'START_NOT_RECORDED',
              title: t('reports.optical.lifecycle.START_NOT_RECORDED'),
            })}
          >
            {count(turnaround.startNotRecorded)}
          </Stat>
          <Stat
            label={t('reports.optical.lifecycle.INCOMPLETE_HISTORY')}
            hint={t('reports.optical.hints.incompleteHistory')}
            action={view({
              kind: 'DELIVERED_JOBS',
              lifecycle: 'INCOMPLETE_HISTORY',
              title: t('reports.optical.lifecycle.INCOMPLETE_HISTORY'),
            })}
          >
            {count(turnaround.incompleteHistory)}
          </Stat>
          <Stat
            label={t('reports.optical.lifecycle.INVALID_HISTORY')}
            hint={t('reports.optical.hints.invalidHistory')}
            action={view({
              kind: 'DELIVERED_JOBS',
              lifecycle: 'INVALID_HISTORY',
              title: t('reports.optical.lifecycle.INVALID_HISTORY'),
            })}
          >
            {count(turnaround.invalidHistory)}
          </Stat>
        </div>
        {periodRecords ? records : null}
        <Card className="min-w-0 p-4">
          <h3 className="text-ink font-semibold">{t('reports.optical.turnaroundByBranch')}</h3>
          <SimpleTable
            headers={[
              col('branch'),
              col('timed'),
              col('average'),
              col('median'),
              col('fastest'),
              col('slowest'),
            ]}
            ends={[1, 2, 3, 4, 5]}
            rows={turnaround.byBranch.map((row) => ({
              key: row.branchId,
              cells: [
                row.branchName,
                count(row.jobs),
                days(row.averageSeconds),
                days(row.medianSeconds),
                days(row.minSeconds),
                days(row.maxSeconds),
              ],
            }))}
          />
        </Card>
        <Card className="min-w-0 p-4">
          <h3 className="text-ink font-semibold">{t('reports.optical.supplyTitle')}</h3>
          <p className="text-muted text-xs">
            {t('reports.optical.supplyNote', { count: supply.jobs })}
          </p>
          <SimpleTable
            headers={[col('supply'), col('lenses'), col('jobs')]}
            ends={[1, 2]}
            rows={[
              ...supply.lenses.map((row) => ({
                key: row.mode,
                cells: [
                  t(`reports.optical.supply.${row.mode}`),
                  count(row.lenses),
                  count(row.jobs),
                ],
              })),
              {
                key: 'none',
                cells: [t('reports.optical.supply.NONE'), '—', count(supply.jobsWithoutLens)],
              },
            ]}
          />
        </Card>
      </Section>
    </div>
  );
}
